import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import React, { useImperativeHandle, useState } from 'react';

export type MbListItem = {
  [key: string]: unknown;
};

export type MbListProps<T extends MbListItem> = {
  records: T[];
  itemRender: (data: T) => React.ReactNode;
  keyName: string;
  selectMax?: number;
  showSelectMaxLabel?: boolean;
  showDividers?: boolean;
  checkboxPlacement?: 'start' | 'end';
  listItemButtonSx?: React.ComponentProps<typeof ListItemButton>['sx'];
};

export type MbListRef = {
  getChecked: () => string[];
  clearChecked: () => void;
};

const MbListInner = <T extends MbListItem>(props: MbListProps<T>, ref: React.ForwardedRef<MbListRef>) => {
  const {
    records,
    itemRender,
    keyName,
    selectMax = 0,
    showSelectMaxLabel = true,
    showDividers = true,
    checkboxPlacement = 'start',
    listItemButtonSx,
  } = props;

  const theme = useTheme();

  const [checked, setChecked] = useState<string[]>([]);

  const handleToggle = (value: string, disabled: boolean) => {
    if (disabled) return;
    const index = checked.indexOf(value);
    const newChecked = [...checked];
    if (index === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(index, 1);
    }
    setChecked(newChecked);
  };

  const getChecked = () => checked;

  const clearChecked = () => setChecked([]);

  useImperativeHandle(ref, () => ({
    getChecked,
    clearChecked,
  }));

  return (
    <Box>
      {selectMax !== 0 && showSelectMaxLabel && (
        <Typography variant="caption" fontWeight="bold">{`最大${selectMax}件チェック`}</Typography>
      )}
      <List>
        {records.map((item) => {
          const { id } = item;

          const isChecked = checked.includes(item[keyName] as string);
          let disabled = false;
          if (selectMax > 0) {
            if (checked.length >= selectMax) {
              disabled = true;
            }
            if (isChecked) {
              disabled = false;
            }
          }

          const selectColor = isChecked ? alpha(theme.palette.primary.main, 0.2) : 'transparent';

          return (
            <React.Fragment key={item[keyName] as string}>
              {showDividers && <Divider />}
              <ListItem key={id as string} sx={{ padding: 0 }}>
                <ListItemButton
                  onClick={() => handleToggle(item[keyName] as string, disabled)}
                  sx={{
                    bgcolor: selectColor,
                    ':hover': { bgcolor: selectColor },
                    ...listItemButtonSx,
                    alignItems: 'center',
                  }}
                  disabled={disabled}
                >
                  {checkboxPlacement === 'start' && (
                    <ListItemIcon sx={{ alignSelf: 'center' }}>
                      <Checkbox value={item[keyName] as string} checked={isChecked} />
                    </ListItemIcon>
                  )}
                  <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{itemRender(item)}</Box>
                  {checkboxPlacement === 'end' && (
                    <ListItemIcon sx={{ minWidth: 'auto', pl: 1, alignSelf: 'center' }}>
                      <Checkbox value={item[keyName] as string} checked={isChecked} />
                    </ListItemIcon>
                  )}
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export const MbList = React.forwardRef(MbListInner);
